<template>
    <div class="search-page">
        <!-- Show loading screen if still loading -->
        <div v-if="isLoading" class="loading-placeholder">
            <div class="shimmer-placeholder amazon-logo-placeholder"></div>
            <div class="shimmer-placeholder title-placeholder"></div>
            <div class="shimmer-placeholder text-placeholder"></div>
            <div class="shimmer-placeholder slider-placeholder"></div>
            <div class="shimmer-placeholder slider-placeholder"></div>
            <div class="shimmer-placeholder button-placeholder"></div>
        </div>

        <!-- Show actual content when loading is done -->
        <div v-else>
            <!-- Amazon Logo -->
            <div class="amazon-logo-container">
                <img :src="`/assets/img/amazon-logo-640.png`" alt="Amazon Logo" class="amazon-logo" />
            </div>

            <!-- Title and Instructions -->
            <div class="page-title">
                <h1>{{settings.siteNameCaps}} Amazon Search</h1>
                <p>
                    Everyone loves a fantastic deal! With our advanced search tool, you can effortlessly find the best Amazon
                    bargains tailored to your preferences. Just enter your desired item, set your price range, and choose your
                    discount level. Let's find those unbeatable deals together!
                </p>
            </div>

            <!-- Search Input -->
            <div class="input-group" ref="searchTerms" id="searchTerms">
                <label for="searchQuery">
                    <span class="slider-label">Search Terms</span>
                </label>
                <input v-model="searchQuery"
                       type="text"
                       id="searchQuery"
                       placeholder="e.g., baby stroller, diapers, toys"
                       class="textbox"
                       aria-label="Search terms"
                       ref="searchInputQuery"
                       @focus="onInputFocus"
                       />
            </div>

            <!-- Price Slider -->
            <div class="input-group">
                <label :for="'maxPrice'" id="maxPriceLabel">
                    <span class="slider-label">Select Price: {{ maxPriceDisplay }}</span>
                </label>
                <input type="range"
                       id="maxPrice"
                       v-model="maxPrice"
                       :min="0"
                       :max="200"
                       :step="5"
                       class="slider"
                       aria-valuemin="0"
                       aria-valuemax="200"
                       :aria-valuenow="maxPrice"
                       aria-label="Price"
                       @click="unfocus"
                       @input="unfocus"/>
            </div>

            <!-- Percent Off Slider -->
            <div class="input-group">
                <label :for="'minPercentOff'" id="minPercentOffLabel">
                    <span class="slider-label">Percent Off: {{ minPercentOffDisplay }}</span>
                </label>
                <input type="range"
                       id="minPercentOff"
                       v-model="minPercentOff"
                       :min="0"
                       :max="99"
                       :step="5"
                       class="slider"
                       aria-valuemin="0"
                       aria-valuemax="99"
                       :aria-valuenow="minPercentOff"
                       aria-label="Percent Off"
                       @click="unfocus"
                       @input="unfocus"/>
            </div>

            <!-- Search Button -->
            <div class="controls-container">
                <div class="deal-button-container">
                    <a :href="amazonUrl" class="button-cta" target="_blank">
                        Search Amazon Deals
                    </a>
                </div>
            </div>

            <!-- Pre-set Links -->
            <div class="preset-links">
                <div class="page-title">
                    <h1>Quick Links</h1>
                </div>
                <div class="links-container">
                    <a v-for="link in presetLinks" :key="link.text" :href="link.url" target="_blank" class="preset-link">
                        {{ link.text }}
                    </a>
                </div>
            </div>

            <!-- Amazon Disclosure -->
            <div class="amazon-disclosure">
                <p>
                    Disclosure: MommyDeals is a participant in the Amazon Services LLC Associates Program, an affiliate advertising
                    program designed to provide a means for us to earn advertising fees by advertising and linking to Amazon.com. As
                    an Amazon Associate we earn from qualifying purchases. Product prices and availability are accurate as of the
                    date/time indicated and are subject to change without notice including promo codes.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import util from '@/helpers/util';
import settings from '@/settings';

// Refs
const searchQuery = ref('');
const maxPrice = ref(0); // 0 indicates disabled
const minPercentOff = ref(0); // 0 indicates disabled
const isLoading = ref(true);
const presetLinks = ref<Array<{ text: string; url: string }>>([]);
const affiliateInfo = ref<{ amazonTag: string; sharedid: string } | null>(null);
const searchTerms = ref<HTMLElement | null>(null);

// Computed
const amazonUrl = computed(() => {
    const baseUrl = 'https://www.amazon.com/s';
    const params = new URLSearchParams();

    if (searchQuery.value.trim()) {
        params.append('k', searchQuery.value.trim());
    }

    const rhParams: string[] = [];

    // Max Price (in cents)
    if (maxPrice.value > 0) {
        const maxPriceCents = maxPrice.value * 100;
        rhParams.push(`p_36:-${maxPriceCents}`);
    }

    // Min Percent Off
    if (minPercentOff.value > 0) {
        const percentOff = minPercentOff.value;
        rhParams.push(`p_75:${percentOff}-`);
    }

    // Free Shipping
    rhParams.push('p_76:2661625011');

    // Combine 'rh' parameters if any
    if (rhParams.length > 0) {
        params.append('rh', rhParams.join(','));
    }

    if (affiliateInfo.value) {
        params.append('tag', affiliateInfo.value.amazonTag);
    }

    return `${baseUrl}?${params.toString()}`;
});

const maxPriceDisplay = computed(() => {
    return maxPrice.value > 0 ? `$${maxPrice.value}` : 'Any';
});

const minPercentOffDisplay = computed(() => {
    return minPercentOff.value > 0 ? `${minPercentOff.value}%` : 'Any';
});

// Methods
const onInputFocus = () => {
    if (isMobileDevice()) {
        scrollToSearchTerms();
    }
};

const isMobileDevice = (): boolean => {
    return window.innerWidth <= 768;
};

const scrollToSearchTerms = () => {
    if (searchTerms.value) {
        searchTerms.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const yOffset = -10;
        const yPosition = searchTerms.value.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: yPosition, behavior: 'smooth' });
    }
};

const unfocus = () => {
    // Empty function as per original
};

const generatePresetUrl = (keyword: string, maxPrice: number): string => {
    const baseUrl = 'https://www.amazon.com/s';
    const params = new URLSearchParams();

    params.append('k', keyword);

    const rhParams: string[] = [];

    // Max Price (in cents)
    const maxPriceCents = maxPrice * 100;
    rhParams.push(`p_36:-${maxPriceCents}`);

    // Free Shipping
    rhParams.push('p_76:2661625011');

    // Combine 'rh' parameters
    params.append('rh', rhParams.join(','));

    if (affiliateInfo.value) {
        params.append('tag', affiliateInfo.value.amazonTag);
    }

    return `${baseUrl}?${params.toString()}`;
};

// Lifecycle hooks
onMounted(() => {
    document.title = settings.siteNameCaps + ' | ' + settings.siteNameCaps + ' Advanced Amazon Search Tool.';

    // Simulate loading time to prevent content shifting
    setTimeout(() => {
        isLoading.value = false;
    }, 1000);

    affiliateInfo.value = util.getAffiliateParams(settings.siteName);

    // Initialize preset links
    presetLinks.value = [
        {
            text: 'Home Essentials under $10',
            url: generatePresetUrl('home essentials', 10),
        },
        {
            text: 'Dresses under $20',
            url: generatePresetUrl('long sleeve dresses under 20', 20),
        },
        {
            text: 'Toys under $10',
            url: generatePresetUrl('toys', 10),
        },
        {
            text: 'Toys under $20',
            url: generatePresetUrl('toys', 20),
        },
        {
            text: 'Toys under $30',
            url: generatePresetUrl('toys', 30),
        },
        {
            text: 'Baby Products under $20',
            url: generatePresetUrl('baby products', 20),
        },
        {
            text: 'Board Games under $25',
            url: generatePresetUrl('board games', 25),
        },
        {
            text: 'Electronics under $50',
            url: generatePresetUrl('electronics', 50),
        },
        {
            text: 'Kitchen Appliances under $30',
            url: generatePresetUrl('kitchen appliances', 30),
        },
        {
            text: 'Kids Books under $15',
            url: generatePresetUrl('books', 15),
        },
        {
            text: 'Beauty Products under $25',
            url: generatePresetUrl('beauty products', 25),
        },
        {
            text: 'Clothing under $20',
            url: generatePresetUrl('clothing', 20),
        },
        {
            text: 'School Supplies under $15',
            url: generatePresetUrl('school supplies', 15),
        },
        {
            text: 'Smart Home Devices under $50',
            url: generatePresetUrl('smart home devices', 50),
        },
        {
            text: 'Outdoor Furniture under $100',
            url: generatePresetUrl('outdoor furniture', 100),
        },
    ];
});
</script>

<style scoped>

    .shimmer-placeholder {
        animation-duration: 1s;
        animation-fill-mode: forwards;
        animation-iteration-count: infinite;
        animation-name: placeHolderShimmer;
        animation-timing-function: linear;
        background: #f6f7f9;
        background-image: linear-gradient(to right, #f6f7f9 0%, #e9ebee 20%, #f6f7f9 40%, #f6f7f9 100%);
        background-repeat: no-repeat;
        background-size: 100%;
    }

    .amazon-logo-placeholder {
        width: 150px;
        height: 60px;
        margin: 0 auto;
    }

    .title-placeholder {
        width: 60%;
        height: 30px;
        margin: 20px auto;
    }

    .text-placeholder {
        width: 80%;
        height: 15px;
        margin: 10px auto;
    }

    .slider-placeholder {
        width: 100%;
        height: 25px;
        margin: 20px 0;
    }

    .button-placeholder {
        width: 200px;
        height: 40px;
        margin: 20px auto;
    }

    /* Shimmer animation */
    @keyframes placeHolderShimmer {
        0% {
            background-position: -468px 0;
        }

        100% {
            background-position: 468px 0;
        }
    }

    /* Base Styles */
    .search-page {
        padding: 30px;
        max-width: 800px;
        margin: 0 auto;
        font-family: 'Roboto', 'Avenir', Helvetica, Arial, sans-serif;
    }

    /* Amazon Logo */
    .amazon-logo-container {
        text-align: center;
        margin-bottom: 20px;
    }

    .amazon-logo {
        max-width: 150px;
        height: auto;
    }

    /* Title and Instructions */
    .page-title {
        text-align: center;
        margin-bottom: 30px;
    }

        .page-title h1 {
            font-size: 28px;
            color: #ff8087;
            margin-bottom: 10px;
        }

        .page-title p {
            font-size: 16px;
            color: #6b6464;
        }

    /* Input Group */
    .input-group {
        margin-bottom: 20px;
        width: 100%;
    }

        .input-group label {
            font-size: 16px;
            color: #ff8087;
            margin-bottom: 5px;
            display: block;
        }

        .input-group input {
            height: 40px;
        }

    .slider-label {
        font-size: 20px; /* Increased font size */
        color: #333;
    }

    .slider {
        width: 100%;
    }

    .textbox {
        width: 100%;
        height: 35px;
        padding: 5px 10px;
        border: 1px solid #bdbdbd;
        font-size: 16px;
        color: #828282;
        border-radius: 3px;
        box-sizing: border-box;
    }

    /* Buttons */
    .controls-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .deal-button-container {
        width: 100%;
        display: flex;
        justify-content: center;
        margin-bottom: 20px;
    }

    .button-cta {
        display: inline-block;
        text-decoration: none;
        min-width: 230px;
        padding: 15px;
        border-radius: 10px;
        background-color: #2945ff;
        color: #fff;
        border: 0;
        font-size: 20px;
        cursor: pointer;
        text-align: center;
    }

        .button-cta:hover,
        .button-cta:focus {
            background-color: #1f34cc;
            outline: none;
        }

    /* Pre-set Links */
    .preset-links {
        text-align: center;
        margin-top: 30px;
    }

        .preset-links p {
            font-size: 18px;
            color: #ff8087;
            margin-bottom: 10px;
        }

    .links-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 10px;
    }

    .preset-link {
        display: inline-block;
        padding: 10px 15px;
        background-color: #e0e0e0;
        color: #333;
        border-radius: 5px;
        text-decoration: none;
    }

        .preset-link:hover,
        .preset-link:focus {
            background-color: #2945ff;
            color: #fff;
            outline: none;
        }

    /* Amazon Disclosure */
    .amazon-disclosure {
        margin-top: 40px;
        font-size: 12px;
        color: #6b6464;
        line-height: 1.5;
    }

        .amazon-disclosure p {
            margin: 0;
            padding: 0 15px;
            text-align: center;
        }

    /* Placeholder Styles */
    .placeholder-container {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .shimmer {
        animation-duration: 1s;
        animation-fill-mode: forwards;
        animation-iteration-count: infinite;
        animation-name: placeHolderShimmer;
        animation-timing-function: linear;
        background: #f6f7f9;
        background-image: linear-gradient( to right, #f6f7f9 0%, #e9ebee 20%, #f6f7f9 40%, #f6f7f9 100% );
        background-repeat: no-repeat;
        background-size: 100%;
        position: relative;
    }

    @keyframes placeHolderShimmer {
        0% {
            background-position: -468px 0;
        }

        100% {
            background-position: 468px 0;
        }
    }

    .shimmer-text {
        width: 100%;
        height: 35px;
    }

    .shimmer-slider {
        width: 100%;
        height: 25px;
    }

    .shimmer-button {
        width: 100%;
        height: 50px;
    }

    /* Responsive Design */
    @media all and (max-width: 768px) {
        .search-page {
            padding: 20px;
        }

        .page-title h1 {
            font-size: 24px;
        }

        .button-cta {
            font-size: 18px;
            padding: 12px;
        }
    }

    @media all and (max-width: 500px) {
        .textbox {
            width: 100%;
        }

        .button-cta {
            width: 100%;
        }

        .page-title h1 {
            font-size: 20px;
        }

        .page-title p {
            font-size: 14px;
        }
    }
</style>
